// Web Battle Pass (Season Pass) — claim actions.
//
// The event is delivered by get-packs (stores.js populates battlePassEvent from
// packsData.seasonPassEvent). Claims are OPTIMISTIC to keep the page snappy and
// the server lightly loaded: we fire one request to grant the rewards, mark the
// milestone claimed locally right away, and DON'T re-fetch to validate — the
// authoritative state is reconciled on the next get-packs (page open / focus,
// see refreshBattlePass). The backend stays the source of truth for what's
// actually granted (premium items only land if the player really owns the pass).
import { get } from 'svelte/store';
import { battlePassEvent, clearExpiredSession, endExpiredSession, isSessionExpired } from '../../stores.js';
import { tagNetworkFailure, tagBodyReadFailure } from '../network-failure.js';
import {
    claim_gold_rush_route,
    claim_season_pass_daily_bonus_route,
    claim_all_season_pass_prizes_route,
    get_store_packs_route,
} from '../../constants.js';

async function postJson(route, body) {
    try {
        const res = await fetch(route, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }).catch(tagNetworkFailure);
        if (isSessionExpired(res)) {
            // The session expired mid-session. Without this the player stayed on a dead
            // page re-clicking a button that could never succeed, and every click fired
            // another error-level report (Rollbar #2519: five in eight seconds from one
            // user). Clear the session like stores.js fetchJson does so App.svelte swaps
            // in <Login/>, and report at info — an expired session is not an app fault.
            endExpiredSession(route);
            return { error: 'sessionExpired' };
        }
        if (!res.ok) {
            Rollbar.error('Web battle pass request failed: ' + route + ' ' + res.status);
            return { error: 'requestFailed' };
        }
        return await res.json().catch(() => ({}));
    } catch (error) {
        // Only a boundary-tagged rejection is a dropped connection; anything else is a fault.
        if (error?.isNetworkFailure) {
            Rollbar.info('Web battle pass request network failure: ' + route + ' ' + error);
        } else {
            Rollbar.error('Web battle pass request error: ' + route + ' ' + error);
        }
        return { error: 'requestFailed' };
    }
}

const currentEvent = () => get(battlePassEvent);

// Targeted, authoritative refresh of ONLY the season pass event (no page Loader).
export async function refreshBattlePass() {
    try {
        const res = await fetch(get_store_packs_route, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ force: true }),
        }).catch(tagNetworkFailure);
        if (isSessionExpired(res)) {
            // This calls get-packs directly rather than through stores.js fetchJson, so it
            // has to clear the session itself — otherwise an expired session here is
            // swallowed silently and the player keeps interacting with stale data.
            clearExpiredSession();
            return;
        }
        if (!res.ok) return;
        const data = await res.json().catch(tagBodyReadFailure);
        if (data && data.seasonPassEvent !== undefined) {
            battlePassEvent.set(data.seasonPassEvent);
        }
    } catch (error) {
        // This refresh runs on page open / focus, so a phone that just lost signal lands here
        // (Rollbar #2440: "Load failed" at warning). Only a boundary-tagged rejection is that;
        // a handler bug stays at warning.
        if (error?.isNetworkFailure) {
            Rollbar.info('Web battle pass refresh network failure: ' + error);
        } else {
            Rollbar.warning('Web battle pass refresh failed: ' + error);
        }
    }
}

// Items a milestone grants: free always (if unclaimed), premium only if the
// player owns the pass (hasEventPass comes from the backend, so this can't be
// faked client-side — and the backend re-checks ownership on the actual grant).
function grantedFor(event, prizeKey) {
    const prize = event.prizes?.[prizeKey];
    if (!prize) return [];
    const claimable = event.claimablePrizes?.[prizeKey] || {};
    let items = [];
    if (!claimable.claimed) items = items.concat(prize.items || []);
    if (event.hasEventPass && !claimable.seasonPassClaimed) items = items.concat(prize.seasonPass || []);
    return items;
}

// Optimistically flip the claim flags locally so the UI updates without a refetch.
function markClaimedLocally(prizeKeys) {
    battlePassEvent.update((event) => {
        const claimablePrizes = { ...(event.claimablePrizes || {}) };
        for (const key of prizeKeys) {
            const flags = { ...(claimablePrizes[key] || {}) };
            flags.claimed = 1;
            if (event.hasEventPass) flags.seasonPassClaimed = 1;
            claimablePrizes[key] = flags;
        }
        return { ...event, claimablePrizes };
    });
}

export async function claimPrize(prizeId) {
    const event = currentEvent();
    const key = 'prize' + prizeId;
    const grantedItems = grantedFor(event, key);

    // Fire the grant (awaited only for the button's loading state); we don't
    // gate the UI on its result — claimed state is reconciled on next refresh.
    await postJson(claim_gold_rush_route, { prizeId, eventId: event.id });
    markClaimedLocally([key]);
    return { grantedItems };
}

export async function claimAll() {
    const event = currentEvent();
    const reached = Object.keys(event.prizes || {}).filter(
        (key) => event.points >= event.prizes[key].pointsNeeded && grantedFor(event, key).length > 0
    );

    let grantedItems = [];
    for (const key of reached) grantedItems = grantedItems.concat(grantedFor(event, key));

    // One backend call claims everything and sends a single consolidated mail.
    await postJson(claim_all_season_pass_prizes_route, { eventId: event.id });
    markClaimedLocally(reached);
    return { grantedItems };
}

export async function claimDailyBonus() {
    const event = currentEvent();
    const result = await postJson(claim_season_pass_daily_bonus_route, { eventId: event.id });
    if (result.error) return result;

    // Optimistic: the request succeeded, so mark the bonus claimed locally — start
    // the cooldown (lastClaimedAt = now) and add the points immediately, without
    // depending on the response shape. The authoritative state lands on next refresh.
    battlePassEvent.update((e) => ({
        ...e,
        points: (e.points || 0) + (e.dailyBonus?.points || 0),
        dailyBonus: { ...(e.dailyBonus || {}), lastClaimedAt: new Date().toISOString() },
    }));
    return {};
}
